import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listProviders, upsertProvider } from '@/api/real-browsers';
import { PROVIDER_KINDS, type ProviderKind } from '@/types';
import { relativeTime } from '@/lib/format';

export default function Providers() {
  const qc = useQueryClient();
  const { data: providers = [], isLoading } = useQuery({
    queryKey: ['providers'],
    queryFn: listProviders,
    refetchInterval: 10_000,
  });

  const [showForm, setShowForm] = useState(false);
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ProviderKind>('remote_vm');
  const [region, setRegion] = useState('');

  const upsertM = useMutation({
    mutationFn: () => upsertProvider({ id, display_name: name || id, kind, region: region || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['providers'] });
      setShowForm(false);
      setId(''); setName(''); setRegion('');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id.trim()) return;
    upsertM.mutate();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">Providers</h1>
          <p className="text-sm text-slate-500">
            Capacity sources — remote VMs, clouds, home rigs, mobile devices,
            bare metal. Each provider hosts one or more pools.
          </p>
        </div>
        <button
          onClick={() => setShowForm(s => !s)}
          className="text-sm px-3 py-1.5 rounded-md bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/40 hover:bg-sky-500/25"
        >
          {showForm ? 'Cancel' : '+ Add provider'}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={onSubmit}
          className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          <Input label="ID (must be unique)" value={id} onChange={setId} placeholder="aws-us-west-2" required mono />
          <Input label="Display name" value={name} onChange={setName} placeholder="AWS US-West-2" />
          <div>
            <label className="block text-xs text-slate-400 mb-1">Kind</label>
            <select
              value={kind}
              onChange={e => setKind(e.target.value as ProviderKind)}
              className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-sm outline-none focus:border-sky-500"
            >
              {PROVIDER_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <Input label="Region" value={region} onChange={setRegion} placeholder="us-west-2" />
          <div className="sm:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={!id.trim() || upsertM.isPending}
              className="px-3 py-1.5 text-sm rounded-md bg-sky-500 text-slate-950 hover:bg-sky-400 disabled:opacity-50"
            >
              {upsertM.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
          {upsertM.isError && (
            <div className="sm:col-span-2 text-xs text-red-300">
              {(upsertM.error as Error).message}
            </div>
          )}
        </form>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-500">loading…</div>
      ) : providers.length === 0 ? (
        <div className="text-sm text-slate-500">No providers yet.</div>
      ) : (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl divide-y divide-slate-800 overflow-hidden">
          {providers.map(p => (
            <div key={p.id} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{p.display_name}</span>
                  <span className="text-[11px] text-slate-500 font-mono shrink-0">{p.kind}</span>
                  {p.region && (
                    <span className="text-[11px] text-slate-500 shrink-0">· {p.region}</span>
                  )}
                </div>
                <p className="text-xs text-slate-500 font-mono truncate mt-0.5">{p.id}</p>
              </div>
              <div className="text-xs text-slate-500 shrink-0">
                {p.enabled ? 'enabled' : 'disabled'} · {relativeTime(p.updated_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, placeholder, required, mono }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; required?: boolean; mono?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}{required && ' *'}</label>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={`w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-sm outline-none focus:border-sky-500 ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}
